import { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { adminLogout } from '@/hooks/useAdminAuth';

interface AdminHeaderProps {
  collapsed: boolean;
  onToggle: () => void;
}

const pageTitles: Record<string, { title: string; desc: string }> = {
  '/admin': { title: '网站访问态势感知', desc: '实时监控网站访问数据与内容热度分析' },
  '/admin/cms/business': { title: '核心业务管理', desc: '管理网站核心业务板块内容' },
  '/admin/cms/ai-products': { title: 'AI产品管理', desc: '管理AI产品矩阵展示内容' },
  '/admin/cms/cases': { title: '客户案例管理', desc: '管理客户成功案例内容' },
  '/admin/cms/news': { title: '新闻动态管理', desc: '管理新闻资讯与行业动态内容' },
};

export default function AdminHeader({ collapsed, onToggle }: AdminHeaderProps) {
  const location = useLocation();
  const navigate = useNavigate();
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);
  const pageInfo = pageTitles[location.pathname] || { title: '管理后台', desc: '' };

  const handleLogout = async () => {
    await adminLogout();
    navigate('/admin/login', { replace: true });
  };

  return (
    <header
      className={`fixed top-0 right-0 h-16 bg-white border-b border-gray-200 flex items-center justify-between px-6 z-30 transition-all duration-300 ${
        collapsed ? 'left-16' : 'left-56'
      }`}
    >
      <div className="flex items-center gap-4">
        <button
          onClick={onToggle}
          className="w-8 h-8 flex items-center justify-center text-gray-500 hover:text-gray-800 hover:bg-gray-100 rounded-lg transition-colors cursor-pointer"
        >
          <i className="ri-menu-line text-lg"></i>
        </button>
        <div>
          <h1 className="text-base font-semibold text-gray-800 leading-tight">{pageInfo.title}</h1>
          {pageInfo.desc && (
            <p className="text-xs text-gray-400 leading-tight">{pageInfo.desc}</p>
          )}
        </div>
      </div>

      <div className="flex items-center gap-3">
        <div className="flex items-center gap-2 px-3 py-1.5 bg-teal-50 rounded-full">
          <div className="w-2 h-2 rounded-full bg-teal-500 animate-pulse"></div>
          <span className="text-xs text-teal-700 font-medium">系统运行正常</span>
        </div>
        <div className="relative">
          <button
            onClick={() => setShowLogoutConfirm((v) => !v)}
            className="flex items-center gap-2 px-3 py-1.5 hover:bg-gray-100 rounded-lg transition-colors cursor-pointer"
          >
            <div className="w-8 h-8 rounded-full bg-gradient-to-br from-teal-500 to-teal-600 flex items-center justify-center">
              <i className="ri-user-line text-white text-sm"></i>
            </div>
            <span className="text-sm text-gray-700 font-medium">管理员</span>
            <i className="ri-arrow-down-s-line text-gray-400 text-sm"></i>
          </button>

          {showLogoutConfirm && (
            <>
              <div
                className="fixed inset-0 z-40"
                onClick={() => setShowLogoutConfirm(false)}
              />
              <div className="absolute right-0 top-full mt-2 w-44 bg-white border border-gray-100 rounded-xl shadow-lg z-50 overflow-hidden">
                <div className="px-4 py-3 border-b border-gray-50">
                  <p className="text-xs text-gray-400">当前登录账号</p>
                  <p className="text-sm font-medium text-gray-800 mt-0.5">admin</p>
                </div>
                <button
                  onClick={handleLogout}
                  className="w-full flex items-center gap-2 px-4 py-2.5 text-sm text-red-600 hover:bg-red-50 transition-colors cursor-pointer whitespace-nowrap"
                >
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-logout-box-line" />
                  </div>
                  退出登录
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </header>
  );
}
